import React from "react";
import Icon from "@mdi/react";
import { mdiBarcodeScan, mdiTrashCanOutline } from "@mdi/js";
import ItemWarehouseSearchField from "./ItemWarehouseSearchField";
import { ItemWarehouseModel } from "../model/warehouse_outward_model";
import "../inward-styles.css";

export type EditableItem = ItemWarehouseModel & {
  availableQty?: number;
};

type Props = {
  item: EditableItem;
  index: number;

  disabledItemSearch?: boolean;
  disableRemove?: boolean;

  onSelectItem: (index: number, item: ItemWarehouseModel) => void;
  onChangeQty: (index: number, qty: number) => void;
  onRemove: (index: number) => void;

  onEditDevices?: () => void;
  id?: string;
};

const EditableWarehouseItemRow: React.FC<Props> = ({
  item,
  index,
  disabledItemSearch = false,
  disableRemove = false,
  onSelectItem,
  onChangeQty,
  onRemove,
  onEditDevices,
  id,
}) => {
  const lineValue = item.requestedQty * (item.itemPrice ?? 0);
  const isFinishedGoods = item.subCategory === "Finished Goods";
  const allocated = item.deviceIds?.length ?? 0;
  const rowId = id ?? `item-row-${index}`;

  return (
    <div id={rowId} className="p-3 border rounded-lg bg-muted/30 space-y-3">
      <div className="grid grid-cols-12 gap-2">
        {/* Select Item */}
        <div className="col-span-4">
          <label className="text-xs font-medium">Select Item *</label>
          <ItemWarehouseSearchField
            id={`${rowId}-item-search`}
            disabled={disabledItemSearch}
            value={item}
            onSelect={(selected) => onSelectItem(index, selected)}
          />
        </div>

        {/* Item Name */}
        <div className="col-span-3">
          <label className="text-xs font-medium">Item Name</label>
          <input
            id={`${rowId}-item-name`}
            disabled
            value={item.itemName}
            title={item.itemName}
            className="h-10 w-full rounded-md border px-3 bg-muted text-sm truncate"
          />
        </div>

        {/* Quantity */}
        <div className="col-span-2">
          <label className="text-xs font-medium">Qty *</label>
          <input
            id={`${rowId}-qty`}
            type="number"
            min={1}
            max={item.availableQty}
            value={item.requestedQty}
            onChange={(e) => onChangeQty(index, Number(e.target.value))}
            className="h-10 w-full rounded-md border px-3 text-sm"
          />
          {item.availableQty !== undefined && (
            <p className="mt-0.5 text-[10px] text-muted-foreground">
              Max {item.availableQty}
            </p>
          )}
        </div>

        {/* Price */}
        <div className="col-span-2">
          <label className="text-xs font-medium">Price *</label>
          <input
            id={`${rowId}-price`}
            type="number"
            min={0}
            value={item.itemPrice ?? 0}
            className="h-10 w-full rounded-md border px-3 text-sm"
            disabled
          />
        </div>

        {/* Remove */}
        <div className="col-span-1 flex items-end justify-center">
          <button
            id={`${rowId}-remove`}
            type="button"
            onClick={() => onRemove(index)}
            disabled={disableRemove}
            title="Remove item"
            className="h-9 w-9 rounded-md text-destructive hover:bg-destructive/10 disabled:opacity-40"
          >
            <Icon path={mdiTrashCanOutline} size={0.8} />
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-between text-sm bg-background p-2 rounded">
        <div>
          <span className="text-muted-foreground">Available:</span>{" "}
          {item.availableQty ?? 0}
        </div>
        <div className="font-medium text-primary">
          Line Value: ₹{lineValue.toLocaleString()}
        </div>
      </div>
      {isFinishedGoods && (
        <div className="flex items-center justify-between p-2 rounded bg-yellow-50 border border-amber-200">
          <div className="flex items-center gap-2 text-sm">
            <Icon
              path={mdiBarcodeScan}
              size={0.8}
              className="text-yellow-600"
            />
            <span className="font-semibold text-yellow-800">
              {allocated} Device IDs allocated
            </span>
          </div>

          <button
            id={`${rowId}-edit-devices`}
            type="button"
            onClick={onEditDevices}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-input bg-white px-3 h-7 text-xs font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
          >
            View / Edit
          </button>
        </div>
      )}
    </div>
  );
};

export default EditableWarehouseItemRow;
